
import { ReactNode, useEffect, useMemo } from "react";
import { useAuthState } from "./useAuthState";
import { AuthContext } from "./AuthContext";
import { useAuthMethods } from "./useAuthMethods";
import { AuthStateListener } from "./AuthStateListener";
import logger from "@/utils/logger";
import { useDeviceMonitor } from "@/hooks/useDeviceMonitor";
import { useDeviceAppVersionSync } from "@/hooks/useDeviceAppVersionSync";
import { useSyncCommandsWatcher } from "@/hooks/sync/useSyncCommandsWatcher";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Use custom hook for auth state management
  const { user, setUser, isLoading, setIsLoading, authInitialized } = useAuthState();
  
  // Use custom hook for auth methods
  const authMethods = useAuthMethods();

  // Memoizziamo il value: cambia riferimento solo quando muta uno stato auth
  // reale, evitando re-render a cascata dei consumer di AuthContext. authMethods
  // e i setter sono già referenzialmente stabili. (#1425)
  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      isLoading,
      authInitialized,
      session: null,
      error: null,
      ...authMethods,
    }),
    [user, setUser, isLoading, authInitialized, authMethods]
  );

  // Attiva il monitoraggio della validità del dispositivo
  useDeviceMonitor();
  useDeviceAppVersionSync(user);
  useSyncCommandsWatcher(user);

  // Light logging fuori dal corpo del render: evita side-effect ad ogni render. (#1425)
  useEffect(() => {
    logger.log("[AUTH_PROVIDER] Autenticazione in caricamento:", isLoading);
  }, [isLoading]);

  return (
    <AuthContext.Provider value={contextValue}>
      <AuthStateListener
        fetchCurrentUser={authMethods.fetchCurrentUser}
        onUserChange={setUser}
        setIsLoading={setIsLoading}
      >
        {children}
      </AuthStateListener>
    </AuthContext.Provider>
  );
};
